// ─── Public Status Page — one incident, in full ──────────────────────
//
// Three states, one component: loading (the summary or the cold deep-link fetch
// is still in flight), notfound (a ref nobody recognises — an old link, a typo)
// and ready. All three keep the back link, because a dead end with no way out is
// the worst thing a public page can do to someone who is already looking for
// help.
//
// The updates run REVERSE-CHRONOLOGICALLY: the newest entry is what the reader
// came for, and making them scroll a maintenance window's twelve updates to find
// out whether it is over would be a peculiar way to run a status page.

import type { MouseEvent } from 'react';
import { impactToComponentStatus, type PublicStatusIncident } from '@pcp/shared';
import { da } from '../i18n/da';
import { fmtDateTime, fmtDuration } from '../lib/format';
import { levelForStatus } from '../lib/levels';
import { isPlainLeftClick } from '../router';
import type { IncidentDetailProps } from '../types';
import { SIcon } from '../ui/SIcon';
import { RichText } from '../ui/RichText';
import { incidentPillLevel, incidentTone } from './IncidentCard';
import { IncidentUpdate } from './IncidentUpdate';

/** Newest first — a copy, so the payload array is never mutated in place. */
function newestFirst(incident: PublicStatusIncident) {
  return [...(incident.updates ?? [])].sort(
    (a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime(),
  );
}

/** "5. aug. 2026 09.00 – 11.00" style window, tolerating a missing end. */
function scheduledWindow(startAt: string | null, endAt: string | null): string | null {
  if (!startAt) return null;
  return endAt ? `${fmtDateTime(startAt)} – ${fmtDateTime(endAt)}` : fmtDateTime(startAt);
}

export function IncidentDetail({ incident, state, onBack, backHref }: IncidentDetailProps) {
  const handleBack = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!isPlainLeftClick(event)) return;
    event.preventDefault();
    onBack();
  };

  const backLink = (
    <a className="sp-back" href={backHref} onClick={handleBack}>
      <SIcon name="chevronLeft" size={16} />
      {da.back}
    </a>
  );

  // `incident` is null for both non-ready states, and a 'ready' with no incident
  // would be a contract violation — treat it as "not found" rather than crash.
  if (!incident) {
    return (
      <div className="sp-detail">
        {backLink}
        {state === 'loading' ? (
          <div className="sp-card sp-card-pad sp-loading" role="status" aria-live="polite">
            <SIcon name="spinner" size={20} className="sp-spin" />
            <span>{da.loading}</span>
          </div>
        ) : (
          <div className="sp-card sp-card-pad sp-empty" role="alert">
            <SIcon name="unknown" size={24} />
            <p>{da.incidentNotFound}</p>
          </div>
        )}
      </div>
    );
  }

  // Roots that are not already named in "Berørte systemer" — see the section below.
  // Computed AFTER the null guard: `incident` is null in both non-ready states.
  const affectedIds = new Set((incident.components ?? []).map((c) => c.id));
  const extraRoots = (incident.roots ?? []).filter((r) => !affectedIds.has(r.id));

  const tone = incidentTone(incident);
  const pill = incidentPillLevel(incident);
  const updates = newestFirst(incident);
  const maintenanceWindow =
    incident.kind === 'maintenance'
      ? scheduledWindow(incident.scheduledStartAt, incident.scheduledEndAt)
      : null;

  return (
    <article className="sp-detail">
      {backLink}

      <header className="sp-detail-head">
        <div className="sp-detail-title-row">
          <SIcon name={tone.icon} size={26} style={{ color: tone.color }} />
          <h1 className="sp-detail-title">{incident.title}</h1>
        </div>

        <div className="sp-detail-meta">
          <span className={`sp-pill sp-pill--${pill.modifier}`}>{incident.statusLabel}</span>
          <span className="sp-incident-ref">{incident.refId}</span>
          <span className="sp-detail-meta-item">
            <SIcon name="clock" size={14} />
            {`${da.incidentStarted} ${fmtDateTime(incident.occurredAt)}`}
          </span>
          {incident.resolvedAt && (
            <>
              <span className="sp-detail-meta-item">
                <SIcon name="operational" size={14} />
                {`${da.incidentResolved} ${fmtDateTime(incident.resolvedAt)}`}
              </span>
              <span className="sp-detail-meta-item">
                {da.incidentDuration(fmtDuration(incident.occurredAt, incident.resolvedAt))}
              </span>
            </>
          )}
          {maintenanceWindow && (
            <span className="sp-detail-meta-item">
              <SIcon name="maintenance" size={14} />
              {`${da.incidentScheduled} ${maintenanceWindow}`}
            </span>
          )}
          {incident.pinned && <span className="sp-incident-pin">{da.incidentPinned}</span>}
        </div>

        {(incident.labels ?? []).length > 0 && (
          <div className="sp-incident-labels">
            {incident.labels.map((label) => (
              <span key={label} className={`sp-label sp-label--${label}`}>
                {da.filterLabels[label]}
              </span>
            ))}
          </div>
        )}
      </header>

      {incident.body && <RichText html={incident.body} />}

      {(incident.components ?? []).length > 0 && (
        <section className="sp-detail-affected">
          <h2 className="sp-detail-affected-title">{da.incidentAffectedComponents}</h2>
          <div className="sp-detail-affected-list">
            {incident.components.map((component) => {
              // 'none' impact = listed but untouched → the neutral pill, never green.
              const level = levelForStatus(impactToComponentStatus(component.impact));
              return (
                <span key={component.id} className={`sp-pill sp-pill--${level.modifier}`}>
                  {component.name}
                </span>
              );
            })}
          </div>
        </section>
      )}

      {/* The top-level services the affected components roll up to — shown only
          when they ADD something. An incident filed directly against a root (the
          common case) would otherwise print the same name twice under two
          headings, which reads as a rendering bug rather than as context. */}
      {extraRoots.length > 0 && (
        <section className="sp-detail-affected">
          <h2 className="sp-detail-affected-title">{da.incidentAffectedGroups}</h2>
          <div className="sp-detail-affected-list">
            {extraRoots.map((root) => (
              <span key={root.id} className="sp-pill">
                {root.name}
              </span>
            ))}
          </div>
        </section>
      )}

      {updates.length > 0 && (
        <section className="sp-detail-updates">
          <h2 className="sp-section-title">{da.incidentUpdates}</h2>
          <div className="sp-updates">
            {updates.map((update) => (
              <IncidentUpdate key={update.id} update={update} impact={incident.impact} />
            ))}
          </div>
        </section>
      )}
    </article>
  );
}
